import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ShieldCheck, AlertTriangle, Clock, MapPin, Loader2, RefreshCw } from "lucide-react";
import api from "../lib/api";

/* Public, view-only Guardian dashboard shared by a group admin. Renders the
   same visual language as /groups/:id/guardian but strictly from the public
   payload (no PII beyond first-names + status labels). */

const STATE = {
  urgent: { dot: "bg-rose-600", tone: "border-rose-300 bg-rose-50", label: "text-rose-800", Icon: AlertTriangle, caption: "Needs attention" },
  attention: { dot: "bg-amber-500", tone: "border-amber-300 bg-amber-50", label: "text-amber-800", Icon: Clock, caption: "Heads up" },
  sharing: { dot: "bg-emerald-500", tone: "border-emerald-300 bg-emerald-50", label: "text-emerald-800", Icon: ShieldCheck, caption: "Sharing" },
  ok: { dot: "bg-emerald-500", tone: "border-emerald-300 bg-emerald-50", label: "text-emerald-800", Icon: ShieldCheck, caption: "OK" },
  not_sharing: { dot: "bg-zinc-300", tone: "border-border bg-card", label: "text-muted-foreground", Icon: MapPin, caption: "Not sharing" },
};

function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function expiresIn(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const hrs = Math.max(0, Math.round((then - Date.now()) / 3600000));
  if (hrs < 1) return "expires in <1h";
  if (hrs < 24) return `expires in ${hrs}h`;
  return `expires in ${Math.floor(hrs / 24)}d`;
}

export default function SharedGuardian() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get("highlight"); // "urgent" | pub_id
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const highlightRef = useRef(null);

  // Spec §12: required disclaimer — Guardian is not a professional emergency service.
  const DISCLAIMER = ("TravelerPulse Guardian helps travelers share information and request "
                       + "assistance from people they trust. It does not replace local emergency "
                       + "services, travel insurance or professional medical and security assistance.");

  const load = async (silent = false) => {
    if (!silent) setBusy(true);
    try {
      const { data } = await api.get(`/groups/guardian/shared/${token}`);
      setData(data); setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "This link is invalid or has expired");
    } finally { if (!silent) setBusy(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ?highlight=urgent | ?highlight=<pub_id> — scroll and pulse the matching row.
  useEffect(() => {
    if (!highlight || !data || !highlightRef.current) return;
    const t = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t);
  }, [highlight, data]);

  if (err) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="shared-guardian-error">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
        <ShieldCheck className="w-10 h-10 mx-auto text-primary/40 mb-3" />
        <p className="font-serif font-800 text-xl text-primary">{err}</p>
        <p className="text-sm text-muted-foreground mt-2">Ask the group admin to send a fresh link.</p>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const counts = data.counts;
  return (
    <div className="min-h-screen bg-background" data-testid="shared-guardian-page">
      <div className="max-w-2xl mx-auto px-5 py-8">
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Group Guardian · view-only</div>
          <h1 className="font-serif font-800 text-2xl text-primary" data-testid="shared-guardian-title">
            {data.group_title}
          </h1>
          {data.destination && <div className="text-sm text-muted-foreground">{data.destination}</div>}
          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
            <span data-testid="shared-guardian-expires">{expiresIn(data.expires_at)}</span>
            <button onClick={() => load(false)} disabled={busy} data-testid="shared-guardian-refresh"
              className="ml-auto inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-60">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
            </button>
          </div>
          {counts && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="shared-guardian-counts">
              <MiniStat label="Total" value={counts.total} tone="text-primary" />
              <MiniStat label="Sharing" value={counts.sharing} tone="text-emerald-700" />
              <MiniStat label="Heads-up" value={counts.attention} tone="text-amber-700" />
              <MiniStat label="Needs help" value={counts.urgent} tone="text-rose-700" />
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-3 mb-4 text-xs text-amber-900" data-testid="guardian-disclaimer">
          <b>Reminder:</b> TravelerPulse Guardian helps travelers share information and request assistance from people they trust. It does not replace local emergency services, travel insurance or professional medical and security assistance.
        </div>

        <ul className="space-y-2" data-testid="shared-guardian-list">
          {(() => {
            const targetIdx = highlight === "urgent"
              ? data.members.findIndex((x) => x.state === "urgent")
              : (highlight ? data.members.findIndex((x) => x.pub_id === highlight) : -1);
            return data.members.map((m, i) => {
              const s = STATE[m.state] || STATE.not_sharing;
              const Icon = s.Icon;
              const isTarget = i === targetIdx && targetIdx >= 0;
              return (
                <li key={i}
                  ref={isTarget ? highlightRef : undefined}
                  className={`border rounded-2xl p-4 ${s.tone} ${isTarget ? "ring-2 ring-rose-500 ring-offset-2 ring-offset-background scroll-mt-24" : ""}`}
                  data-testid={`shared-guardian-row-${i}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-700 text-sm truncate">{m.name}</div>
                    <div className={`text-xs ${s.label} font-700 inline-flex items-center gap-1`}>
                      <Icon className="w-3 h-3" /> {s.caption}
                      {m.latest_status && (
                        <span className="text-muted-foreground font-500">
                          {" · "}{m.latest_status.status_label} · {relTime(m.latest_status.occurred_at)}
                        </span>
                      )}
                    </div>
                    {m.session?.journey_label && (
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {m.session.journey_label}{m.session.destination ? ` → ${m.session.destination}` : ""}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          });
          })()}
        </ul>

        <p className="text-[11px] text-muted-foreground text-center mt-6">
          This is a temporary view-only link. Coordinates are never shared here — only the label each traveller chose to share.
          Signed in as a group member? Open the full dashboard from Group → Guardian.
        </p>
      </div>
    </div>
  );
}

const MiniStat = ({ label, value, tone }) => (
  <div className="border border-border bg-background rounded-xl px-3 py-2">
    <div className={`font-serif font-800 text-xl leading-none ${tone}`}>{value ?? 0}</div>
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);
